import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, CreditCard, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const accountTypes = {
  checking: { label: "Vadesiz Hesap", icon: Building2, color: "sky" },
  savings: { label: "Vadeli Hesap", icon: Wallet, color: "emerald" },
  credit_card: { label: "Kredi Kartı", icon: CreditCard, color: "amber" }
};

const BankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    bank_name: "",
    account_type: "checking",
    account_number: "",
    iban: "",
    balance: "0",
    credit_limit: "",
    notes: ""
  });

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`, { withCredentials: true });
      setAccounts(response.data);
    } catch (error) {
      toast.error("Hesaplar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        balance: parseFloat(formData.balance) || 0,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null
      };
      
      if (editingAccount) {
        await axios.put(`${API}/bank-accounts/${editingAccount.id}`, data, { withCredentials: true });
        toast.success("Hesap güncellendi");
      } else {
        await axios.post(`${API}/bank-accounts`, data, { withCredentials: true });
        toast.success("Hesap eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hesabı silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/bank-accounts/${id}`, { withCredentials: true });
      toast.success("Hesap silindi");
      fetchAccounts();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      bank_name: account.bank_name,
      account_type: account.account_type,
      account_number: account.account_number || "",
      iban: account.iban || "",
      balance: account.balance.toString(),
      credit_limit: account.credit_limit?.toString() || "",
      notes: account.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      name: "",
      bank_name: "",
      account_type: "checking",
      account_number: "",
      iban: "",
      balance: "0",
      credit_limit: "",
      notes: ""
    });
  };

  const totalBalance = accounts
    .filter(a => a.account_type !== 'credit_card')
    .reduce((sum, a) => sum + a.balance, 0);
  
  const totalCreditDebt = accounts
    .filter(a => a.account_type === 'credit_card')
    .reduce((sum, a) => sum + Math.abs(a.balance), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Banka Hesapları</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Toplam Bakiye: <span className="text-emerald-400">{formatCurrency(totalBalance)}</span>
            {totalCreditDebt > 0 && (
              <> | Kart Borcu: <span className="text-rose-400">{formatCurrency(totalCreditDebt)}</span></>
            )}
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Hesap
        </Button>
      </div>

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((account, index) => {
          const typeInfo = accountTypes[account.account_type] || accountTypes.checking;
          const Icon = typeInfo.icon;
          const isCredit = account.account_type === 'credit_card';
          
          return (
            <motion.div
              key={account.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => openEditDialog(account)}
              className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 cursor-pointer hover:border-zinc-700 transition-colors"
              data-testid={`account-card-${account.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-sm bg-${typeInfo.color}-400/10`}>
                    <Icon className={`w-5 h-5 text-${typeInfo.color}-400`} />
                  </div>
                  <div>
                    <p className="text-zinc-50 font-medium">{account.name}</p>
                    <p className="text-xs text-zinc-500">{account.bank_name}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Pencil className="w-3 h-3 text-zinc-500 mt-2" />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); handleDelete(account.id); }}
                    className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400 h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 mb-1">
                    {isCredit ? 'Mevcut Borç' : 'Bakiye'}
                  </p>
                  <p className={`text-2xl font-mono ${
                    isCredit 
                      ? (account.balance < 0 ? 'text-rose-400' : 'text-emerald-400')
                      : (account.balance >= 0 ? 'text-emerald-400' : 'text-rose-400')
                  }`}>
                    {isCredit && account.balance < 0 ? '-' : ''}
                    {formatCurrency(Math.abs(account.balance))}
                  </p>
                </div>

                {isCredit && account.credit_limit && (
                  <div>
                    <p className="text-xs text-zinc-500 mb-1">Kullanılabilir Limit</p>
                    <p className="text-sm font-mono text-zinc-400">
                      {formatCurrency(account.credit_limit + account.balance)}
                    </p>
                    <div className="mt-2 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-400 transition-all"
                        style={{ width: `${Math.min(100, (Math.abs(account.balance) / account.credit_limit) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {account.iban && (
                  <div>
                    <p className="text-xs text-zinc-500 mb-1">IBAN</p>
                    <p className="text-xs font-mono text-zinc-400 break-all">{account.iban}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-zinc-800">
                <span className={`text-xs px-2 py-1 rounded-sm bg-${typeInfo.color}-400/10 text-${typeInfo.color}-400`}>
                  {typeInfo.label}
                </span>
              </div>
            </motion.div>
          );
        })}
        {accounts.length === 0 && (
          <div className="col-span-full py-12 text-center text-zinc-500">
            Henüz hesap eklenmemiş
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingAccount ? "Hesap Düzenle" : "Yeni Hesap"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Hesap Adı *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Örn: Ana Hesap, İş Kartı"
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Banka *</Label>
                <Input 
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  required
                  placeholder="Örn: Garanti, Yapı Kredi"
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Hesap Türü *</Label>
                <Select value={formData.account_type} onValueChange={(v) => setFormData({ ...formData, account_type: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(accountTypes).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Hesap No</Label>
                <Input 
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">
                  {formData.account_type === 'credit_card' ? 'Mevcut Borç (₺)' : 'Bakiye (₺)'} *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              {formData.account_type === 'credit_card' && (
                <div>
                  <Label className="text-zinc-400">Kredi Limiti (₺)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              )}
              <div className={formData.account_type === 'credit_card' ? '' : 'col-span-2'}>
                <Label className="text-zinc-400">IBAN</Label>
                <Input 
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="TR..."
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
              >
                {editingAccount ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccounts;
